import { InputHTMLAttributes, useCallback, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import {
  COLOR_PRIMARY,
  COLOR_LIGHT_RED,
  COLOR_RED,
} from "../../constants/color";
import { CloseIcon } from "./icons/CloseIcon";
import { ErrorIcon } from "./icons/input/ErrorIcon";
import { EyeCrossedIcon } from "./icons/input/EyeCrossedIcon";
import { EyeIcon } from "./icons/input/EyeIcon";

type Props = {
  name: string;
  uuid?: string;
  label?: string;
  type?: InputHTMLAttributes<HTMLInputElement>["type"];
  placeholder?: string;
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  helperText?: string;
  autoComplete?: string;
  clearable?: boolean;
  fullWidth?: boolean;
  maxLength?: number;
  className?: string;
};

export const Input: React.FC<Props> = ({
  name,
  uuid,
  label,
  type,
  placeholder,
  defaultValue,
  value,
  onChange,
  required,
  disabled,
  autoFocus,
  hasError,
  errorMessage,
  helperText,
  autoComplete,
  clearable,
  fullWidth,
  maxLength,
  className,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const formContext = useFormContext();
  const { register, setValue } = formContext ?? {};

  const [showPassword, setShowPassword] = useState<boolean>(false);

  const handleOnClear = useCallback(() => {
    if (onChange) onChange("");
    if (formContext) formContext.setValue(name, "", { shouldValidate: true });

    if (!inputRef.current) return;

    inputRef.current.value = "";
  }, [formContext, inputRef, onChange]);

  return (
    <StyledContainer $fullWidth={fullWidth} className={className}>
      <StyledHeader>
        {label && (
          <StyledLabel>
            {required ? (
              <>
                {label.replace(' *', '')} <StyledRequiredAsterisk>*</StyledRequiredAsterisk>
              </>
            ) : (
              label
            )}
          </StyledLabel>
        )}
      </StyledHeader>

      <StyledInputContainer>
        <StyledInput
          id={uuid ? `input-${uuid}` : undefined}
          {...register?.(name)}
          ref={register?.(name).ref ?? inputRef}
          onChange={(e) => {
            if (onChange) onChange(e.target.value);
            register?.(name).onChange(e);
          }}
          type={showPassword ? "text" : type}
          placeholder={placeholder}
          defaultValue={defaultValue}
          value={value}
          disabled={disabled}
          maxLength={maxLength}
          onInput={(e) =>
          (e.currentTarget.value =
            (type === "number" &&
              e.currentTarget.value.slice(0, maxLength)) ||
            e.currentTarget.value)
          }
          autoComplete={autoComplete ?? "off"}
          autoFocus={autoFocus}
          $hasError={hasError}
          $isPassword={type === "password"}
          $clearable={clearable}
          onWheel={(e) => {
            if (type === 'number') {
              e.currentTarget.blur();
            }
          }}
        />

        {type === "password" && (
          <>
            <StyledIcon
              id={`input-password-icon-${uuid}`}
              onClick={() => setShowPassword((previousValue) => !previousValue)}
            >
              {showPassword ? <EyeIcon /> : <EyeCrossedIcon />}
            </StyledIcon>
          </>
        )}

        {clearable && (
          <StyledIcon
            id={`input-clear-icon-${uuid}`}
            onClick={handleOnClear}
            style={{ marginRight: "10px" }}
          >
            <CloseIcon uuid={uuid} />
          </StyledIcon>
        )}
      </StyledInputContainer>

      {helperText && (
        <StyledHelperTextContainer $hasError={hasError}>
          <StyledHelperTextIconContainer>※</StyledHelperTextIconContainer>
          <StyledHelperTextMessage>{helperText}</StyledHelperTextMessage>
        </StyledHelperTextContainer>
      )}

      {hasError && errorMessage && (
        <StyledErrorMessageContainer>
          <StyledErrorIconContainer>
            <ErrorIcon />
          </StyledErrorIconContainer>
          <StyledErrorMessage>{errorMessage}</StyledErrorMessage>
        </StyledErrorMessageContainer>
      )}
    </StyledContainer>
  );
};

const StyledContainer = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${({ $fullWidth }) => $fullWidth && "width: 100%;"}
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
`;

const StyledLabel = styled.label`
  font-size: 14px;
  font-weight: 400;
  line-height: 100%;
  margin-right: 5px;
`;

const StyledRequiredAsterisk = styled.span`
  color: ${COLOR_RED};
`;

const StyledInputContainer = styled.div`
  position: relative;
`;

const StyledInput = styled.input<{
  $hasError?: boolean;
  $isPassword: boolean;
  $clearable?: boolean;
}>`
  @media (max-width: 990px) {
    font-size: 16px;
  }
  width: 100%;
  height: 55px;
  padding: 0 16px;
  ${({ $isPassword, $clearable }) =>
    ($isPassword || $clearable) && 'padding-right: 48px;'}
  border-radius: 8px;
  border: 1px solid #cccccc;
  font-size: 14px;
  line-height: 100%;
  outline: none;
  background-color: #ffffff;
  box-sizing: border-box;

  @media (max-width: 768px) {
    height: 48px;
    border-radius: 6px;
  }

  @media (max-width: 480px) {
    height: 44px;
    border-radius: 4px;
  }

  &::placeholder {
    color: #999999;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"]::-webkit-contacts-auto-fill-button {
    display: none !important;
    visibility: hidden !important;
    pointer-events: none !important;
    position: absolute;
    right: 0;
  }

  ${({ $hasError }) =>
    $hasError &&
    `
      border-color: ${COLOR_RED};
      background-color: ${COLOR_LIGHT_RED};
    `}

  &:focus {
    border-color: ${({ $hasError }) =>
    $hasError ? COLOR_RED : "#0A2463"};
  }

  &:disabled {
    background-color: #f2f2f2;
    border-color: #cccccc;
    color: #999999;
    cursor: not-allowed;

    &::placeholder {
      color: #999999;
    }
  }
`;

const StyledIcon = styled.div`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 8px;
  outline: none;
  &:focus {
    outline: none;
  }
`;

const StyledHelperTextContainer = styled.div<{ $hasError?: boolean }>`
  display: flex;
  align-items: flex-start;
  ${({ $hasError }) => $hasError && `color: ${COLOR_RED};`}
`;

const StyledHelperTextIconContainer = styled.div`
  margin-right: 10px;
`;

const StyledHelperTextMessage = styled.div`
  font-size: 13px;
  font-weight: 400;
  line-height: 150%;
`;

const StyledErrorMessageContainer = styled.div`
  display: flex;
  align-items: flex-start;
`;

const StyledErrorIconContainer = styled.div`
  margin-top: 3px;
  margin-right: 8px;
`;

const StyledErrorMessage = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  color: ${COLOR_RED};
`;
